import { api } from './client';
import type {
  AnalyticsSeriesResponse,
  AssociateAnalytics,
  AssociateFeedbacksQuery,
  AssociateFeedbacksResponse,
  AssociateProfile,
  AssociateServiceItem,
  KycDocType,
  KycDocUploadUrlResponse,
  KycStatusResponse,
} from '../types/api';

// ---- Feedbacks ---------------------------------------------
export async function getAssociateFeedbacks(
  query?: AssociateFeedbacksQuery,
): Promise<AssociateFeedbacksResponse> {
  const { data } = await api.get<AssociateFeedbacksResponse>('/associates/me/feedbacks', {
    params: query,
  });
  return data;
}

// ---- Profile -----------------------------------------------
export async function getAssociateProfile(): Promise<AssociateProfile> {
  const { data } = await api.get<AssociateProfile>('/associates/me/profile');
  return data;
}

export interface UpdateAssociateProfilePayload {
  outletName?: string;
  pincode?: string;
  state?: string;
  district?: string;
  taluk?: string;
  village?: string;
  landmark?: string;
  street?: string;
}

export async function updateAssociateProfile(
  payload: UpdateAssociateProfilePayload,
): Promise<AssociateProfile> {
  const { data } = await api.patch<AssociateProfile>('/associates/me/profile', payload);
  return data;
}

// ---- KYC ---------------------------------------------------
export async function getKycStatus(): Promise<KycStatusResponse> {
  const { data } = await api.get<KycStatusResponse>('/associates/me/kyc');
  return data;
}

export async function requestKycDocUploadUrl(
  docType: KycDocType,
  contentType: 'image/jpeg' | 'image/png' | 'application/pdf',
  contentLength: number,
): Promise<KycDocUploadUrlResponse> {
  const { data } = await api.post<KycDocUploadUrlResponse>(
    '/associates/me/kyc/documents/upload-url',
    { docType, contentType, contentLength },
  );
  return data;
}

// Two-phase: presigned PUT → return the S3 key for the submit payload.
export async function uploadKycDoc(docType: KycDocType, file: File): Promise<string> {
  const mime = (file.type || 'application/pdf') as 'image/jpeg' | 'image/png' | 'application/pdf';
  if (!['image/jpeg', 'image/png', 'application/pdf'].includes(mime)) {
    throw new Error('KYC docs must be JPG, PNG, or PDF.');
  }
  if (file.size > 10 * 1024 * 1024) throw new Error('File must be under 10 MB.');

  try {
    const presigned = await requestKycDocUploadUrl(docType, mime, file.size);
    const headers: Record<string, string> = {};
    if (presigned.headers) {
      for (const [k, v] of Object.entries(presigned.headers)) {
        if (k.toLowerCase() !== 'content-length') {
          headers[k] = v;
        }
      }
    }
    if (!headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = mime;
    }
    const put = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers,
      body: file,
    });
    if (put.ok) return presigned.key;
  } catch (err) {
    console.warn('Presigned KYC upload failed, attempting direct upload fallback:', err);
  }

  // Fallback: Direct multipart upload
  const formData = new FormData();
  formData.append('file', file);
  formData.append('docType', docType);

  const { data } = await api.post<{ key: string }>(
    '/associates/me/kyc/documents/upload-direct',
    formData,
  );
  return data.key;
}

export interface SubmitKycPayload {
  aadhaarNumber: string;
  aadhaarDocS3Key: string;
  panNumber: string;
  panDocS3Key?: string;
  gstin?: string;
  address: string;
  bankAccountNumber: string;
  bankName: string;
  bankBranch: string;
  ifscCode: string;
}

export async function submitKyc(payload: SubmitKycPayload): Promise<KycStatusResponse> {
  const { data } = await api.post<KycStatusResponse>('/associates/me/kyc', payload);
  return data;
}

// ---- Services offered --------------------------------------
export async function listMyServices(): Promise<AssociateServiceItem[]> {
  const { data } = await api.get<{ items: AssociateServiceItem[] }>('/associates/me/services');
  return data.items;
}

export async function setMyServices(serviceIds: string[]): Promise<AssociateServiceItem[]> {
  const { data } = await api.put<{ items: AssociateServiceItem[] }>('/associates/me/services', {
    serviceIds,
  });
  return data.items;
}

// ---- Analytics ---------------------------------------------
export async function getAssociateAnalytics(): Promise<AssociateAnalytics> {
  const { data } = await api.get<AssociateAnalytics>('/associates/me/analytics');
  return data;
}

export async function getAssociateAnalyticsSeries(year: number): Promise<AnalyticsSeriesResponse> {
  const { data } = await api.get<AnalyticsSeriesResponse>('/associates/me/analytics/series', {
    params: { year },
  });
  return data;
}

export async function downloadAnalyticsReport(from: string, to: string): Promise<Blob> {
  const { data } = await api.get<Blob>('/associates/me/analytics/report.csv', {
    params: { from, to },
    responseType: 'blob',
  });
  return data;
}
